<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserPermissions } from '../../composables/usePermissions';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import DataTable from '../../grid/DataTable.vue';
import { imageColumn, productNameColumn, franchiseColumn, priceColumn } from '../../grid/productTableColumns';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

const emit = defineEmits<{
  edit: [productId: Id];
  adjust: [productId: Id, franchiseId: Id];
  movements: [productId: Id];
}>();

// Get i18n composable
const { t, n } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed<string>(() => '/index.php/so/stock/lista');

// Ajustar/editar são gerenciais no backend (stock/adjust e products/get são
// Full/Franqueado/Gerente): esconder aqui evita oferecer ao Atendente botões
// que só falhariam na API. A leitura (lista/movimentos) continua para todos.
const { isManagerOrAbove } = useUserPermissions();

const tableColumns = computed(() => {
  return [
    {
      data: 'so_stock_product_id',
      title: t('Código'),
      width: 140,
      sortable: true,
      ...(isManagerOrAbove.value ? { renderType: 'clickable_id' } : {}),
      mergedColumns: [
        franchiseColumn(t, 'so_stock_franchise_name'),
        { data: 'so_stock_updated_at', title: t('Última Atualização'), renderType: 'datetime' },
      ]
    },
    imageColumn(t),
    {
      ...productNameColumn(t, 280),
    },
    {
      data: 'so_product_franchise_id',
      title: t('Tipo'),
      width: 140,
      sortable: true,
      renderType: 'badge',
      badgeConfig: (row: Record<string, unknown>) => ({
        label: row.so_product_franchise_id ? t('Produto da franquia') : t('Catálogo global'),
        severity: row.so_product_franchise_id ? 'info' : 'success'
      })
    },
    {
      data: 'so_stock_quantity',
      title: t('Quantidade'),
      width: 120,
      sortable: true,
      mergedColumns: [
        {
          data: 'so_stock_minimum_quantity',
          title: t('Alerta estoque baixo'),
          cellRenderer: ({ value }: { value: unknown }): string => {
            const num = typeof value === 'number' ? value : parseFloat(String(value ?? 0)) || 0;
            return `${t('Alerta estoque baixo')}: ${num}`;
          }
        }
      ]
    },
    {
      data: 'so_stock_is_low',
      title: t('Situação'),
      width: 130,
      sortable: true,
      renderType: 'badge',
      filterType: 'select',
      filterOptions: [
        { label: t('Estoque baixo'), value: true },
        { label: t('Normal'), value: false }
      ],
      badgeConfig: (row: Record<string, unknown>) => {
        const isLow = row.so_stock_is_low === true;
        return {
          label: isLow ? t('Estoque baixo') : t('Normal'),
          severity: isLow ? 'warn' : 'secondary'
        };
      }
    },
    {
      ...priceColumn(t, 'so_stock_effective_price', t('Preço efetivo'), 130),
      mergedColumns: [
        {
          data: 'so_product_default_price',
          title: t('Preço base'),
          cellRenderer: ({ value }: { value: unknown }): string => {
            const num = typeof value === 'number' ? value : parseFloat(String(value ?? 0)) || 0;
            return `${t('Preço base')}: ${n(num, 'currency')}`;
          }
        }
      ]
    },
    ...(isManagerOrAbove.value ? [{
      data: null,
      title: t('Ações'),
      width: 100,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Ajustar'),
      buttonIcon: 'pi pi-box',
      buttonAction: 'adjust',
      buttonTestId: (data: Record<string, unknown>): string => {
        const productId = String(data.so_stock_product_id ?? '').replace(/[^0-9]/g, '');
        const franchiseId = String(data.so_stock_franchise_id ?? '').replace(/[^0-9]/g, '');
        return `stock-table-adjust-btn-${productId}-${franchiseId}`;
      }
    }] : [])
  ];
});

const datatableConfig = computed(() => {
  return {
    dataKey: 'so_stock_id',
    filename: `estoque_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small',
    rowClass: (row: Record<string, unknown>) => {
      const qty = Number(row.so_stock_quantity ?? 0);
      const min = Number(row.so_stock_minimum_quantity ?? 0);
      return min > 0 && qty <= min ? 'so-stock-low-row' : '';
    }
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

// Methods
const loadData = (): void => {
  log('SOStockTable', 'loadData', 'Triggering table refresh');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('SOStockTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de estoque'));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('SOStockTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar estoque') + ': ' + message);
};

const handleEditClick = (productId: Id): void => {
  log('SOStockTable', 'handleEditClick', 'Edit clicked for product', productId);
  emit('edit', productId);
};

const handleButtonClick = (payload: { value: unknown; data: Record<string, unknown> | null; action: unknown }): void => {
  const productId = payload.data?.so_stock_product_id as Id | undefined;
  if (!productId) return;
  if (payload.action === 'adjust') {
    const franchiseId = payload.data?.so_stock_franchise_id as Id | undefined;
    if (!franchiseId) {
      log('SOStockTable', 'handleButtonClick', 'Missing so_stock_franchise_id — adjust aborted');
      return;
    }
    log('SOStockTable', 'handleButtonClick', 'Adjust clicked', { productId, franchiseId });
    emit('adjust', productId, franchiseId);
  }
};

// Event handlers (extracted for proper off() removal)
const handleFilterUpdated = (): void => {
  log('SOStockTable', 'event:filter.updated', 'Received');
  loadData();
};

const handleStockAdjusted = (): void => {
  log('SOStockTable', 'event:so-stock.adjusted', 'Received');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('SOStockTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  log('SOStockTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  // Listen for filter updates
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('so-stock.adjusted', handleStockAdjusted);
});

onBeforeUnmount(() => {
  log('SOStockTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('so-stock.adjusted', handleStockAdjusted);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
/* Low-stock rows (quantity at/below a configured minimum) get an amber tint. */
:deep(.so-stock-low-row > td) {
  background: rgba(234, 179, 8, 0.13);
}
</style>
